import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { authFetch } from "@/lib/authFetch";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Key, Plus, Copy, Trash2, Clock, Shield, ChevronLeft, Menu, Check, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApiKeysPage() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRawKey, setNewRawKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api-keys`);
      if (res.ok) {
        const data = await res.json();
        setKeys(data.keys || []);
      }
    } catch (err) {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) { toast.error("Key name is required"); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API}/api-keys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newKeyName.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewRawKey(data.raw_key);
        setNewKeyName("");
        fetchKeys();
        toast.success("API key created");
      } else {
        toast.error("Failed to create key");
      }
    } catch {
      toast.error("Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId, keyName) => {
    if (!window.confirm(`Revoke "${keyName}"? Any automation using this key will stop working immediately.`)) return;
    try {
      const res = await authFetch(`${API}/api-keys/${keyId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Key revoked");
        fetchKeys();
      } else {
        toast.error("Failed to revoke key");
      }
    } catch {
      toast.error("Failed to revoke key");
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newRawKey);
    setCopied(true);
    toast.success("Key copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const closeKeyDialog = () => {
    setNewRawKey(null);
    setShowCreateDialog(false);
    setCopied(false);
  };

  const activeKeys = keys.filter(k => k.is_active);
  const revokedKeys = keys.filter(k => !k.is_active);

  return (
    <div className="flex min-h-screen bg-zinc-950 text-zinc-100">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={true} />
      <div className="flex-1 lg:ml-72">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-zinc-800">
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                <h1 className="text-lg font-semibold">API Keys</h1>
              </div>
            </div>
            <button
              data-testid="create-api-key-btn"
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-medium rounded-lg transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Create Key
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-6 max-w-4xl">
          {/* Info Banner */}
          <div className="mb-6 p-4 rounded-lg border border-zinc-800 bg-zinc-900/50">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-zinc-400 space-y-1">
                <p>API keys grant <strong className="text-zinc-200">full admin access</strong> to all portal endpoints.</p>
                <p>Use the <code className="px-1.5 py-0.5 bg-zinc-800 rounded text-amber-300 text-xs">X-API-Key</code> header to authenticate requests from your automation tools.</p>
                <p className="text-zinc-500 mt-2 font-mono text-xs">curl -H "X-API-Key: cfiak_..." {process.env.REACT_APP_BACKEND_URL}/api/clients?page=1</p>
              </div>
            </div>
          </div>

          {/* Create Dialog */}
          {showCreateDialog && !newRawKey && (
            <div className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <h3 className="text-sm font-medium text-zinc-200 mb-3">New API Key</h3>
              <div className="flex gap-2">
                <input
                  data-testid="api-key-name-input"
                  type="text"
                  placeholder="Key name (e.g., Zapier Integration)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  autoFocus
                />
                <button
                  data-testid="confirm-create-key-btn"
                  onClick={handleCreate}
                  disabled={creating}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-medium rounded-lg text-sm disabled:opacity-50 transition-colors"
                >
                  {creating ? "Creating..." : "Create"}
                </button>
                <button onClick={closeKeyDialog} className="px-3 py-2 text-zinc-400 hover:text-zinc-200 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Raw Key Display (shown once after creation) */}
          {newRawKey && (
            <div className="mb-6 p-4 rounded-lg border border-amber-500/50 bg-amber-500/10">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-300">Copy your API key now</h3>
                  <p className="text-xs text-zinc-400 mt-1">This is the only time it will be shown. Store it in a secure location.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="raw-api-key-display"
                  className="flex-1 px-3 py-2.5 bg-zinc-950 border border-zinc-700 rounded-lg text-xs text-amber-300 font-mono break-all select-all"
                >
                  {newRawKey}
                </code>
                <button
                  data-testid="copy-api-key-btn"
                  onClick={copyKey}
                  className="flex items-center gap-1.5 px-3 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm transition-colors flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <button
                onClick={closeKeyDialog}
                className="mt-3 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                I've stored it securely — close this
              </button>
            </div>
          )}

          {/* Active Keys */}
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Loading...</div>
          ) : activeKeys.length === 0 && revokedKeys.length === 0 ? (
            <div className="text-center py-16">
              <Key className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No API keys yet</p>
              <p className="text-zinc-600 text-xs mt-1">Create one to start integrating with external tools</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeKeys.map((k) => (
                <div key={k.key_id} data-testid={`api-key-row-${k.key_id}`} className="flex items-center justify-between p-4 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-200 truncate">{k.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                        <span className="font-mono">{k.key_prefix}...</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Created {new Date(k.created_at).toLocaleDateString()}
                        </span>
                        {k.last_used_at && (
                          <span>Last used {new Date(k.last_used_at).toLocaleDateString()}</span>
                        )}
                        {k.created_by_name && <span>by {k.created_by_name}</span>}
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid={`revoke-key-${k.key_id}`}
                    onClick={() => handleRevoke(k.key_id, k.name)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg text-xs transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Revoke
                  </button>
                </div>
              ))}

              {/* Revoked Keys */}
              {revokedKeys.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2">Revoked</p>
                  {revokedKeys.map((k) => (
                    <div key={k.key_id} className="flex items-center justify-between p-3 rounded-lg border border-zinc-800/50 bg-zinc-950 opacity-50 mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-red-500/50" />
                        <div>
                          <p className="text-sm text-zinc-500 line-through">{k.name}</p>
                          <span className="text-xs text-zinc-600 font-mono">{k.key_prefix}...</span>
                        </div>
                      </div>
                      <span className="text-xs text-zinc-600">Revoked {k.revoked_at ? new Date(k.revoked_at).toLocaleDateString() : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
